/**
 * M7 S34: the bundle picker (spec §2.5, §8.1), shown at a hand-in only. It renders a `BundleView` and a
 * `BundleState` and dispatches `BundleAction`s; every rule is in `models/bundle.ts`. No counts: a full bundle says
 * so in words. The opposition's line for every link left out is previewed. UI events only.
 */
import type { ReactNode } from 'react'
import type { BundleAction, BundleState, BundleView } from './models/bundle'
import type { ClaimPanelConfig } from './models/claim'

export interface BundlePickerProps {
  view: BundleView
  state: BundleState
  config: ClaimPanelConfig
  dispatch: (a: BundleAction) => void
}

function focused(on: boolean): { 'data-focused'?: '' } {
  return on ? { 'data-focused': '' } : {}
}

export function BundlePicker(p: BundlePickerProps): ReactNode {
  const { view, state: s } = p
  const b = p.config.bundle
  return (
    <section className="sui lg-screen" data-testid="bundle-picker" role="dialog" aria-label={view.title}>
      <div className="sui-head">
        <h2 className="sui-title">{view.title}</h2>
      </div>
      <p className="lg-row__meta">{view.intro}</p>
      <ul className="lg-list">
        {view.rows.map((r, i) => (
          <li key={r.key} className="lg-row" data-testid="bundle-row" data-key={r.key} {...focused(s.row === i)}>
            <button
              type="button"
              tabIndex={-1}
              className="sui-btn lg-btn"
              aria-pressed={r.picked}
              data-testid="bundle-toggle"
              {...focused(s.row === i)}
              onClick={() => p.dispatch({ type: 'press', index: i })}
            >
              {r.title}
            </button>
            {r.note === null ? null : <span className="lg-row__meta">{r.note}</span>}
            {r.picked ? <span className="lg-mark">{b.picked}</span> : null}
          </li>
        ))}
      </ul>
      {view.full ? <p className="lg-row__meta">{b.full}</p> : null}
      <section className="lg-section" data-testid="bundle-opposition">
        <h3 className="lg-section__title">{b.oppositionHeading}</h3>
        {view.opposition.length === 0 ? (
          <p className="sui-empty">{b.noGaps}</p>
        ) : (
          <ul className="lg-list">
            {view.opposition.map((line) => (
              <li key={line} className="lg-row" data-testid="bundle-missing">
                {line}
              </li>
            ))}
          </ul>
        )}
      </section>
      {s.notice === null ? null : (
        <p className="lg-notice" role="status" data-testid="bundle-notice">
          {s.notice}
        </p>
      )}
      <div className="sui-head">
        <button
          type="button"
          tabIndex={-1}
          className="sui-btn lg-btn"
          data-testid="bundle-hand-in"
          {...focused(s.row === view.rows.length)}
          onClick={() => p.dispatch({ type: 'handIn' })}
        >
          {b.handIn}
        </button>
        <button type="button" tabIndex={-1} className="sui-btn lg-btn" data-testid="bundle-back" onClick={() => p.dispatch({ type: 'nav', input: 'back' })}>
          {b.back}
        </button>
      </div>
    </section>
  )
}
